import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, LogIn, LogOut } from "lucide-react";
import Logo from "@/components/app/Logo";

function fmtTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
  } catch { return ""; }
}

export default function TapRegister() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against StrictMode double-invoke -> avoid double registration
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/attendance/tap/${token}`);
        setResult(data);
        setState("ok");
      } catch (e) {
        setError(e.response?.data?.detail || "No se pudo registrar");
        setState("error");
      }
    })();
  }, [token]);

  const isIn = result?.type === "in";

  return (
    <div className="min-h-screen bg-nardo-50 flex flex-col">
      <div className="h-[3px] w-full bg-navy" />
      <header className="max-w-2xl w-full mx-auto px-6 py-6 flex items-center justify-between">
        <Logo />
        <span className="text-[11px] sm:text-xs font-mono text-nardo-500">uaicardaccess.emergentagent.com</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-16" data-testid="tap-register-page">
        {state === "loading" && (
          <div className="text-nardo-500 uppercase tracking-[0.3em] text-xs" data-testid="tap-loading">Registrando…</div>
        )}

        {state === "ok" && (
          <div className={`w-full max-w-md rounded-md p-8 text-center relative ${isIn ? "bg-navy text-white" : "bg-white border border-nardo-200"}`} data-testid="tap-success">
            <div className={`absolute top-0 left-0 right-0 h-[2px] ${isIn ? "bg-emerald-300" : "bg-red-400"}`} />
            <div className="flex justify-center mb-4">
              {isIn
                ? <LogIn className="w-14 h-14 text-emerald-300" strokeWidth={1.5} />
                : <LogOut className="w-14 h-14 text-red-500" strokeWidth={1.5} />}
            </div>
            <div className={`text-[10px] uppercase tracking-[0.35em] mb-2 ${isIn ? "text-emerald-300" : "text-red-500"}`} data-testid="tap-type">
              {isIn ? "Entrada registrada" : "Salida registrada"}
            </div>
            <div className={`font-display font-extrabold text-3xl ${isIn ? "text-white" : "text-navy"}`} data-testid="tap-name">{result.student_name}</div>
            {result.grade && <div className={`text-sm mt-1 ${isIn ? "text-nardo-200" : "text-nardo-500"}`}>{result.grade}</div>}
            <div className={`font-mono text-lg mt-4 ${isIn ? "text-emerald-200" : "text-navy"}`} data-testid="tap-time">{fmtTime(result.timestamp)}</div>
            {result.school_name && <div className={`text-xs mt-3 ${isIn ? "text-nardo-300" : "text-nardo-400"}`}>{result.school_name}</div>}
            <CheckCircle2 className={`w-5 h-5 mx-auto mt-5 ${isIn ? "text-emerald-300" : "text-emerald-600"}`} />
          </div>
        )}

        {state === "error" && (
          <div className="w-full max-w-md bg-red-50 border border-red-200 rounded-md p-8 text-center" data-testid="tap-error">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <div className="text-[10px] uppercase tracking-[0.35em] text-red-600 mb-2">No se registró</div>
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}
      </main>
    </div>
  );
}
